'use client';
import React, { isValidElement, createElement } from 'react';
import { FrameworkHooks } from '@trv-tds/webcomponents';
import { defineCustomElements } from '@trv-tds/webcomponents/dist/loader';

const rootMap = new WeakMap();
let reactDOMClient;
let renderImpl;
var handleExternalContent = (content, container) => {
    const check = Array.isArray(content) ? content[0] : content;
    if (check && isValidElement(check)) {
        // if array of one, render just the one to avoid the warning about lists needing keys
        const element = Array.isArray(content) && content.length === 1 ? content[0] : content;
        render(element, container);
        return true;
    }
    return false;
};
const clearContainer = (container) => {
    if (reactDOMClient) {
        const root = rootMap.get(container);
        if (root) {
            root.unmount();
            rootMap.delete(container);
        }
    }
    // not sure how to clear react 17 or less container
};
function render(content, container) {
    if (!renderImpl) {
        setRenderImpl();
    }
    renderImpl(content, container);
}
function renderWithReactDOMClient(content, container) {
    let root = rootMap.get(container);
    if (!root) {
        root = reactDOMClient.createRoot(container);
        rootMap.set(container, root);
    }
    root.render(content);
}
function renderWithReactDOM(content, container) {
    const reactDOM = require('react-dom');
    reactDOM.render(content, container);
}
function setRenderImpl() {
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // To prevent a module not found error during a CI build where process.env.CI=true, apps using React17 
    // should set process.env.REACT_APP_TDS_REACT17=true using an .env file or setting it in the CI build.
    // (See https://create-react-app.dev/docs/adding-custom-environment-variables/ and https://webpack.js.org/plugins/define-plugin/)
    // define process.env in case `process.env.REACT_APP_TDS_REACT17` is not replaced by DefinePlugin
    const mockProcess = { env: {} };
    globalThis.process = globalThis.process || mockProcess;
    globalThis.process.env = globalThis.process.env || mockProcess.env;
    // will become if('true' === 'true') when configured which will remove the whole block of code after minification
    renderImpl = renderWithReactDOM;
    if (process.env.REACT_APP_TDS_REACT17 !== 'true') {
        try {
            reactDOMClient = require('react-dom/client');
            renderImpl = renderWithReactDOMClient;
        }
        catch {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('TDS Warning: If using React 17 or less, you may have to set environment variable REACT_APP_TDS_REACT17=true in your CI build to prevent a build error. More information is available of the TDS docsite at "Get Started" => "Development" => "React" => "If Using React"');
            }
        }
    }
    if (globalThis.process === mockProcess)
        delete globalThis.process;
}

const dashToPascalCase = (str) => str
    .toLowerCase()
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
const camelToDashCase = (str) => str.replace(/([A-Z])/g, (m) => `-${m[0].toLowerCase()}`);

const attachProps = (node, newProps, oldProps = {}) => {
    // some test frameworks don't render DOM elements, so we test here to make sure we are dealing with DOM first
    if (node instanceof Element) {
        // add any classes in className to the class list
        const className = getClassName(node.classList, newProps, oldProps);
        if (className !== '') {
            node.className = className;
        }
        Object.keys(newProps).forEach((name) => {
            if (name === 'children' ||
                name === 'style' ||
                name === 'ref' ||
                name === 'class' ||
                name === 'className' ||
                name === 'forwardedRef') {
                return;
            }
            if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                const eventName = name.substring(2);
                const eventNameLc = eventName[0].toLowerCase() + eventName.substring(1);
                if (!isCoveredByReact(eventNameLc)) {
                    syncEvent(node, eventNameLc, newProps[name]);
                }
            }
            else {
                node[name] = newProps[name];
                const propType = typeof newProps[name];
                if (propType === 'string') {
                    node.setAttribute(camelToDashCase(name), newProps[name]);
                }
            }
        });
    }
};
const getClassName = (classList, newProps, oldProps) => {
    const newClassProp = newProps.className || newProps.class;
    const oldClassProp = oldProps.className || oldProps.class;
    // map the classes to Maps for performance
    const currentClasses = arrayToMap(classList);
    const incomingPropClasses = arrayToMap(newClassProp ? newClassProp.split(' ') : []);
    const oldPropClasses = arrayToMap(oldClassProp ? oldClassProp.split(' ') : []);
    const finalClassNames = [];
    // loop through each of the current classes on the component
    // to see if it should be a part of the classNames added
    currentClasses.forEach((currentClass) => {
        if (incomingPropClasses.has(currentClass)) {
            // add it as its already included in classnames coming in from newProps
            finalClassNames.push(currentClass);
            incomingPropClasses.delete(currentClass);
        }
        else if (!oldPropClasses.has(currentClass)) {
            // add it as it has NOT been removed by user
            finalClassNames.push(currentClass);
        }
    });
    incomingPropClasses.forEach((s) => finalClassNames.push(s));
    return finalClassNames.join(' ');
};
/**
 * Transforms a React event name to a browser event name.
 */
const transformReactEventName = (eventNameSuffix) => {
    switch (eventNameSuffix) {
        case 'doubleclick':
            return 'dblclick';
    }
    return eventNameSuffix;
};
/**
 * Checks if an event is supported in the current execution environment.
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
const isCoveredByReact = (eventNameSuffix) => {
    if (typeof document === 'undefined') {
        return true;
    }
    else {
        const eventName = 'on' + transformReactEventName(eventNameSuffix);
        let isSupported = eventName in document;
        if (!isSupported) {
            const element = document.createElement('div');
            element.setAttribute(eventName, 'return;');
            isSupported = typeof element[eventName] === 'function';
        }
        return isSupported;
    }
};
const syncEvent = (node, eventName, newEventHandler) => {
    const eventStore = node.__events || (node.__events = {});
    const oldEventHandler = eventStore[eventName];
    // Remove old listener so they don't double up.
    if (oldEventHandler) {
        node.removeEventListener(eventName, oldEventHandler);
    }
    // Bind new listener.
    node.addEventListener(eventName, (eventStore[eventName] = function handler(e) {
        if (newEventHandler) {
            newEventHandler.call(this, e);
        }
    }));
};
const arrayToMap = (arr) => {
    const map = new Map();
    arr.forEach((s) => map.set(s, s));
    return map;
};

const setRef = (ref, value) => {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref != null) {
        // Cast as a MutableRef so we can assign current
        ref.current = value;
    }
};
const mergeRefs = (...refs) => {
    return (value) => {
        refs.forEach((ref) => {
            setRef(ref, value);
        });
    };
};
const createForwardRef = (ReactComponent, displayName) => {
    const forwardRef = (props, ref) => {
        return React.createElement(ReactComponent, { ...props, forwardedRef: ref });
    };
    forwardRef.displayName = displayName;
    return React.forwardRef(forwardRef);
};

const createReactComponent = (tagName, ReactComponentContext, manipulatePropsFunction, defineCustomElement) => {
    if (defineCustomElement !== undefined) {
        defineCustomElement();
    }
    const displayName = dashToPascalCase(tagName);
    const ReactComponent = class extends React.Component {
        constructor(props) {
            super(props);
            this.setComponentElRef = (element) => {
                this.componentEl = element;
            };
        }
        componentDidMount() {
            this.componentDidUpdate(this.props);
        }
        componentDidUpdate(prevProps) {
            attachProps(this.componentEl, this.props, prevProps);
        }
        render() {
            const { children, forwardedRef, style, className, ref, ...cProps } = this.props;
            let propsToPass = Object.keys(cProps).reduce((acc, name) => {
                const value = cProps[name];
                if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                    const eventName = name.substring(2).toLowerCase();
                    if (typeof document !== 'undefined' && isCoveredByReact(eventName)) {
                        acc[name] = value;
                    }
                }
                else {
                    // we should only render strings, booleans, and numbers as attrs in html.
                    // objects, functions, arrays etc get synced via properties on mount.
                    const type = typeof value;
                    if (type === 'string' || type === 'boolean' || type === 'number') {
                        acc[camelToDashCase(name)] = value;
                    }
                }
                return acc;
            }, {});
            if (manipulatePropsFunction) {
                propsToPass = manipulatePropsFunction(this.props, propsToPass);
            }
            const newProps = {
                ...propsToPass,
                ref: mergeRefs(forwardedRef, this.setComponentElRef),
                style,
            };
            /**
             * We use createElement here instead of
             * React.createElement to work around a
             * bug in Vite (https://github.com/vitejs/vite/issues/6104).
             * React.createElement causes all elements to be rendered
             * as <tagname> instead of the actual Web Component.
             */
            return createElement(tagName, newProps, children);
        }
        static get displayName() {
            return displayName;
        }
    };
    // If context was passed to createReactComponent then conditionally add it to the Component Class
    if (ReactComponentContext) {
        ReactComponent.contextType = ReactComponentContext;
    }
    return createForwardRef(ReactComponent, displayName);
};

/* eslint-disable */
defineCustomElements();
const TdsAccordion = /*@__PURE__*/ createReactComponent('tds-accordion');
const TdsAccordionItem = /*@__PURE__*/ createReactComponent('tds-accordion-item');
const TdsAlert = /*@__PURE__*/ createReactComponent('tds-alert');
const TdsAlertSitewide = /*@__PURE__*/ createReactComponent('tds-alert-sitewide');
const TdsBreadcrumbs = /*@__PURE__*/ createReactComponent('tds-breadcrumbs');
const TdsButton = /*@__PURE__*/ createReactComponent('tds-button');
const TdsButtonContainer = /*@__PURE__*/ createReactComponent('tds-button-container');
const TdsCalendar = /*@__PURE__*/ createReactComponent('tds-calendar');
const TdsCard = /*@__PURE__*/ createReactComponent('tds-card');
const TdsCardContent = /*@__PURE__*/ createReactComponent('tds-card-content');
const TdsCellEditor = /*@__PURE__*/ createReactComponent('tds-cell-editor');
const TdsCheck = /*@__PURE__*/ createReactComponent('tds-check');
const TdsCombobox = /*@__PURE__*/ createReactComponent('tds-combobox');
const TdsDateInput = /*@__PURE__*/ createReactComponent('tds-date-input');
const TdsExpandedCellContent = /*@__PURE__*/ createReactComponent('tds-expanded-cell-content');
const TdsField = /*@__PURE__*/ createReactComponent('tds-field');
const TdsFileupload = /*@__PURE__*/ createReactComponent('tds-fileupload');
const TdsFormatter = /*@__PURE__*/ createReactComponent('tds-formatter');
const TdsGlobalFooter = /*@__PURE__*/ createReactComponent('tds-global-footer');
const TdsGlobalFooterCap = /*@__PURE__*/ createReactComponent('tds-global-footer-cap');
const TdsGlobalFooterLinks = /*@__PURE__*/ createReactComponent('tds-global-footer-links');
const TdsGlobalHeader = /*@__PURE__*/ createReactComponent('tds-global-header');
const TdsGlobalHeaderDropdown = /*@__PURE__*/ createReactComponent('tds-global-header-dropdown');
const TdsIconButton = /*@__PURE__*/ createReactComponent('tds-icon-button');
const TdsLoadingSpinner = /*@__PURE__*/ createReactComponent('tds-loading-spinner');
const TdsModal = /*@__PURE__*/ createReactComponent('tds-modal');
const TdsModalButtons = /*@__PURE__*/ createReactComponent('tds-modal-buttons');
const TdsMultiselect = /*@__PURE__*/ createReactComponent('tds-multiselect');
const TdsNav = /*@__PURE__*/ createReactComponent('tds-nav');
const TdsNavItem = /*@__PURE__*/ createReactComponent('tds-nav-item');
const TdsNavList = /*@__PURE__*/ createReactComponent('tds-nav-list');
const TdsNavToggle = /*@__PURE__*/ createReactComponent('tds-nav-toggle');
const TdsPagination = /*@__PURE__*/ createReactComponent('tds-pagination');
const TdsProgress = /*@__PURE__*/ createReactComponent('tds-progress');
const TdsSearchBox = /*@__PURE__*/ createReactComponent('tds-search-box');
const TdsSelect = /*@__PURE__*/ createReactComponent('tds-select');
const TdsSelectedOptions = /*@__PURE__*/ createReactComponent('tds-selected-options');
const TdsSwitch = /*@__PURE__*/ createReactComponent('tds-switch');
const TdsTable = /*@__PURE__*/ createReactComponent('tds-table');
const TdsTableColumn = /*@__PURE__*/ createReactComponent('tds-table-column');
const TdsTableEditable = /*@__PURE__*/ createReactComponent('tds-table-editable');
const TdsTableExpandableRows = /*@__PURE__*/ createReactComponent('tds-table-expandable-rows');
const TdsTablePagination = /*@__PURE__*/ createReactComponent('tds-table-pagination');
const TdsTableRowSelection = /*@__PURE__*/ createReactComponent('tds-table-row-selection');
const TdsTableSort = /*@__PURE__*/ createReactComponent('tds-table-sort');
const TdsTableStacked = /*@__PURE__*/ createReactComponent('tds-table-stacked');
const TdsTag = /*@__PURE__*/ createReactComponent('tds-tag');
const TdsTagContainer = /*@__PURE__*/ createReactComponent('tds-tag-container');
const TdsTimeInput = /*@__PURE__*/ createReactComponent('tds-time-input');
const TdsTimePicker = /*@__PURE__*/ createReactComponent('tds-time-picker');
const TdsTooltip = /*@__PURE__*/ createReactComponent('tds-tooltip');
const TdsTopHat = /*@__PURE__*/ createReactComponent('tds-top-hat');
const TdsValidator = /*@__PURE__*/ createReactComponent('tds-validator');

FrameworkHooks.isFrameworkElement = isValidElement;
FrameworkHooks.handleExternalContent = handleExternalContent;
FrameworkHooks.clearContainer = clearContainer;

export { TdsAccordion, TdsAccordionItem, TdsAlert, TdsAlertSitewide, TdsBreadcrumbs, TdsButton, TdsButtonContainer, TdsCalendar, TdsCard, TdsCardContent, TdsCellEditor, TdsCheck, TdsCombobox, TdsDateInput, TdsExpandedCellContent, TdsField, TdsFileupload, TdsFormatter, TdsGlobalFooter, TdsGlobalFooterCap, TdsGlobalFooterLinks, TdsGlobalHeader, TdsGlobalHeaderDropdown, TdsIconButton, TdsLoadingSpinner, TdsModal, TdsModalButtons, TdsMultiselect, TdsNav, TdsNavItem, TdsNavList, TdsNavToggle, TdsPagination, TdsProgress, TdsSearchBox, TdsSelect, TdsSelectedOptions, TdsSwitch, TdsTable, TdsTableColumn, TdsTableEditable, TdsTableExpandableRows, TdsTablePagination, TdsTableRowSelection, TdsTableSort, TdsTableStacked, TdsTag, TdsTagContainer, TdsTimeInput, TdsTimePicker, TdsTooltip, TdsTopHat, TdsValidator };
//# sourceMappingURL=index.esm.js.map
